import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Target, TrendingUp, TrendingDown, MapPin, Users, Star, Filter, Search, Navigation, Building2 } from 'lucide-react';
import { KPICard } from '../components/ui/KPICard';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { ResponsiveContainer, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid } from 'recharts';
// Radar chart data - ALDI Nord is now "us", EDEKA is a competitor
const radarData = [{
  category: 'Preis-Leistung',
  'ALDI Nord': 88,
  EDEKA: 72,
  REWE: 70,
  LIDL: 85
}, {
  category: 'Frische',
  'ALDI Nord': 68,
  EDEKA: 88,
  REWE: 82,
  LIDL: 70
}, {
  category: 'Freundlichkeit',
  'ALDI Nord': 70,
  EDEKA: 82,
  REWE: 79,
  LIDL: 68
}, {
  category: 'Sauberkeit',
  'ALDI Nord': 78,
  EDEKA: 85,
  REWE: 83,
  LIDL: 76
}, {
  category: 'Auswahl',
  'ALDI Nord': 65,
  EDEKA: 90,
  REWE: 85,
  LIDL: 68
}, {
  category: 'Erreichbarkeit',
  'ALDI Nord': 85,
  EDEKA: 75,
  REWE: 80,
  LIDL: 88
}];
// Win/Loss data - ALDI Nord vs competitors
const competitorAnalysis = [{
  location: 'Hamburg-Hafencity',
  aldiNord: 'ALDI Nord Hamburg',
  competitor: 'EDEKA Center',
  competitorType: 'EDEKA',
  distance: '0.8 km',
  status: 'winning',
  aldiRating: 4.6,
  competitorRating: 4.2,
  insight: 'Bessere Preise und schnellere Kassen',
  customerFlow: '+12%'
}, {
  location: 'Berlin-Kreuzberg',
  aldiNord: 'ALDI Nord Berlin',
  competitor: 'REWE',
  competitorType: 'REWE',
  distance: '0.3 km',
  status: 'neutral',
  aldiRating: 3.8,
  competitorRating: 3.9,
  insight: 'Gleichstand - Auswahl verbessern',
  customerFlow: '-2%'
}, {
  location: 'Köln-Ehrenfeld',
  aldiNord: 'ALDI Nord Köln',
  competitor: 'EDEKA',
  competitorType: 'EDEKA',
  distance: '0.5 km',
  status: 'losing',
  aldiRating: 3.2,
  competitorRating: 4.0,
  insight: 'Sauberkeit und Frische verbessern',
  customerFlow: '-15%'
}, {
  location: 'Frankfurt-Sachsenhausen',
  aldiNord: 'ALDI Nord Frankfurt',
  competitor: 'REWE',
  competitorType: 'REWE',
  distance: '0.6 km',
  status: 'winning',
  aldiRating: 4.2,
  competitorRating: 3.8,
  insight: 'Preis-Leistung überzeugt Kunden',
  customerFlow: '+8%'
}, {
  location: 'Düsseldorf-Bilk',
  aldiNord: 'ALDI Nord Düsseldorf',
  competitor: 'LIDL',
  competitorType: 'LIDL',
  distance: '0.4 km',
  status: 'neutral',
  aldiRating: 4.3,
  competitorRating: 4.2,
  insight: 'Direkter Discount-Wettbewerb',
  customerFlow: '+3%'
}, {
  location: 'Dortmund-Innenstadt',
  aldiNord: 'ALDI Nord Dortmund',
  competitor: 'EDEKA',
  competitorType: 'EDEKA',
  distance: '0.7 km',
  status: 'losing',
  aldiRating: 3.4,
  competitorRating: 4.1,
  insight: 'Wartezeiten reduzieren',
  customerFlow: '-10%'
}];
const competitorColors: Record<string, string> = {
  EDEKA: '#fce530',
  REWE: '#cc061d',
  LIDL: '#0050AA'
};
// Regional rating data
const regionalRatings = [{
  region: 'Nord',
  'ALDI Nord': 4.2,
  EDEKA: 4.4,
  REWE: 3.9,
  LIDL: 4.0,
  markets: 18
}, {
  region: 'Ost',
  'ALDI Nord': 4.1,
  EDEKA: 4.2,
  REWE: 3.8,
  LIDL: 3.9,
  markets: 22
}, {
  region: 'West',
  'ALDI Nord': 3.9,
  EDEKA: 4.3,
  REWE: 4.0,
  LIDL: 4.1,
  markets: 25
}, {
  region: 'Mitte',
  'ALDI Nord': 4.0,
  EDEKA: 4.1,
  REWE: 3.9,
  LIDL: 3.8,
  markets: 12
}];
// Radius-based mock data
const radiusData: Record<string, {
  name: string;
  type: string;
  rating: number;
  distance: string;
}[]> = {
  'Hamburg, Jungfernstieg': [{
    name: 'ALDI Nord HafenCity',
    type: 'ALDI Nord',
    rating: 4.3,
    distance: '0.6 km'
  }, {
    name: 'EDEKA Center',
    type: 'EDEKA',
    rating: 4.1,
    distance: '0.4 km'
  }, {
    name: 'REWE City',
    type: 'REWE',
    rating: 3.9,
    distance: '0.2 km'
  }, {
    name: 'LIDL Hamburg-Mitte',
    type: 'LIDL',
    rating: 3.7,
    distance: '0.8 km'
  }, {
    name: 'ALDI Nord Neustadt',
    type: 'ALDI Nord',
    rating: 4.0,
    distance: '1.1 km'
  }],
  'Berlin, Alexanderplatz': [{
    name: 'ALDI Nord Mitte',
    type: 'ALDI Nord',
    rating: 3.9,
    distance: '0.3 km'
  }, {
    name: 'EDEKA Express',
    type: 'EDEKA',
    rating: 3.7,
    distance: '0.5 km'
  }, {
    name: 'REWE Berlin-Mitte',
    type: 'REWE',
    rating: 4.1,
    distance: '0.5 km'
  }, {
    name: 'LIDL Alexanderplatz',
    type: 'LIDL',
    rating: 4.0,
    distance: '0.7 km'
  }],
  'Köln, Neumarkt': [{
    name: 'ALDI Nord Köln-Zentrum',
    type: 'ALDI Nord',
    rating: 4.1,
    distance: '0.4 km'
  }, {
    name: 'EDEKA Köln',
    type: 'EDEKA',
    rating: 4.3,
    distance: '0.6 km'
  }, {
    name: 'REWE Neumarkt',
    type: 'REWE',
    rating: 4.0,
    distance: '0.3 km'
  }, {
    name: 'LIDL Köln-Zentrum',
    type: 'LIDL',
    rating: 3.8,
    distance: '0.9 km'
  }]
};
const regions = ['Alle Regionen', 'Nord', 'Ost', 'West', 'Mitte'];
const radiusOptions = ['1 km', '2 km', '5 km', '10 km'];
const competitors = ['ALDI Nord', 'EDEKA', 'REWE', 'LIDL'];
const competitorBarColors: Record<string, string> = {
  'ALDI Nord': '#00005f',
  EDEKA: '#fce530',
  REWE: '#cc061d',
  LIDL: '#0050AA'
};
export function CompetitorRadarPage() {
  const [filterMode, setFilterMode] = useState<'region' | 'radius'>('region');
  const [selectedRegion, setSelectedRegion] = useState('Alle Regionen');
  const [addressInput, setAddressInput] = useState('');
  const [selectedRadius, setSelectedRadius] = useState('5 km');
  const [addressSearched, setAddressSearched] = useState('Hamburg, Jungfernstieg');
  const [hasSearched, setHasSearched] = useState(true);
  const winningCount = competitorAnalysis.filter(c => c.status === 'winning').length;
  const losingCount = competitorAnalysis.filter(c => c.status === 'losing').length;
  const filteredRegionalData = selectedRegion === 'Alle Regionen' ? regionalRatings : regionalRatings.filter(r => r.region === selectedRegion);
  const regionBarData = filteredRegionalData.map(r => ({
    region: r.region,
    'ALDI Nord': r['ALDI Nord'],
    EDEKA: r.EDEKA,
    REWE: r.REWE,
    LIDL: r.LIDL
  }));
  const radiusStores = radiusData[addressSearched] || radiusData['Hamburg, Jungfernstieg'];
  const radiusAggregated = competitors.map(type => {
    const stores = radiusStores.filter(s => s.type === type);
    const avg = stores.length > 0 ? stores.reduce((acc, s) => acc + s.rating, 0) / stores.length : null;
    return {
      name: type,
      rating: avg,
      count: stores.length
    };
  }).filter(d => d.rating !== null);
  const handleSearch = () => {
    const key = Object.keys(radiusData).find(k => k.toLowerCase().includes(addressInput.toLowerCase().split(',')[0].toLowerCase())) || 'Hamburg, Jungfernstieg';
    setAddressSearched(key);
    setHasSearched(true);
  };
  return <div className="p-6 space-y-6 overflow-y-auto h-[calc(100vh-64px)]" data-id="element-398">
      {/* Header KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-399">
        <KPICard title="Marktposition" value="#1" change="Discounter" trend="up" icon={<Target size={18} data-id="element-401" />} subtext="in Nord-Deutschland" data-id="element-400" />
        <KPICard title="Gewonnene Standorte" value={winningCount} change={`von ${competitorAnalysis.length}`} trend="up" icon={<TrendingUp size={18} data-id="element-403" />} subtext="im 5km Radius" data-id="element-402" />
        <KPICard title="Verlorene Standorte" value={losingCount} change="Handlungsbedarf" trend="down" icon={<TrendingDown size={18} data-id="element-405" />} subtext="an Konkurrenz" data-id="element-404" />
        <KPICard title="Ø Rating-Vorsprung" value="+0.2" change="vs. Wettbewerb" trend="up" icon={<Users size={18} data-id="element-407" />} subtext="Sterne" data-id="element-406" />
      </div>

      {/* Rating Comparison Card */}
      <Card data-id="element-408">
        <div className="p-6" data-id="element-409">
          <div className="flex items-start justify-between mb-5 flex-wrap gap-4" data-id="element-410">
            <div data-id="element-411">
              <h3 className="text-lg font-semibold text-gray-900" data-id="element-412">
                ALDI Nord vs. Mitbewerber – Bewertungsvergleich
              </h3>
              <p className="text-sm text-gray-500 mt-0.5" data-id="element-413">
                Durchschnittliche Google-Bewertungen im direkten Vergleich
              </p>
            </div>
            <div className="flex items-center bg-gray-100 rounded-lg p-1 gap-1" data-id="element-414">
              <button onClick={() => setFilterMode('region')} className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${filterMode === 'region' ? 'bg-white text-aldi-blue shadow-sm' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-415">
                <Building2 size={14} data-id="element-416" />
                Region
              </button>
              <button onClick={() => setFilterMode('radius')} className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${filterMode === 'radius' ? 'bg-white text-aldi-blue shadow-sm' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-417">
                <Navigation size={14} data-id="element-418" />
                Umkreis
              </button>
            </div>
          </div>

          <AnimatePresence mode="wait" data-id="element-419">
            {filterMode === 'region' ? <motion.div key="region" initial={{
            opacity: 0,
            y: 8
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -8
          }} transition={{
            duration: 0.18
          }} data-id="element-420">
                <div className="flex items-center gap-3 mb-5 flex-wrap" data-id="element-421">
                  <div className="flex items-center gap-2 text-sm font-medium text-gray-600" data-id="element-422">
                    <Filter size={14} data-id="element-423" />
                    <span data-id="element-424">Region:</span>
                  </div>
                  <div className="flex flex-wrap gap-2" data-id="element-425">
                    {regions.map(region => <button key={region} onClick={() => setSelectedRegion(region)} className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${selectedRegion === region ? 'bg-aldi-blue text-white shadow-sm' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-id="element-426">
                        {region}
                      </button>)}
                  </div>
                </div>

                <div className="h-64" data-id="element-427">
                  <ResponsiveContainer width="100%" height="100%" data-id="element-428">
                    <BarChart data={regionBarData} margin={{
                  top: 5,
                  right: 20,
                  left: 0,
                  bottom: 5
                }} barCategoryGap="25%" barGap={3} data-id="element-429">
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} data-id="element-430" />
                      <XAxis dataKey="region" axisLine={false} tickLine={false} tick={{
                    fontSize: 11,
                    fill: '#6b7280'
                  }} data-id="element-431" />
                      <YAxis domain={[3, 5]} axisLine={false} tickLine={false} tick={{
                    fontSize: 11,
                    fill: '#9ca3af'
                  }} tickFormatter={v => v.toFixed(1) + ' ★'} data-id="element-432" />
                      <Tooltip contentStyle={{
                    borderRadius: '8px',
                    border: 'none',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                    fontSize: '12px'
                  }} formatter={(value: number, name: string) => [value.toFixed(1) + ' ★', name]} data-id="element-433" />
                      {competitors.map(comp => <Bar key={comp} dataKey={comp} name={comp} fill={competitorBarColors[comp]} radius={[3, 3, 0, 0]} fillOpacity={comp === 'ALDI Nord' ? 1 : 0.55} data-id="element-434" />)}
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                <div className="flex items-center justify-center gap-6 mt-3 flex-wrap" data-id="element-435">
                  {competitors.map(comp => <div key={comp} className="flex items-center gap-1.5" data-id="element-436">
                      <div className="w-3 h-3 rounded-sm" style={{
                  backgroundColor: competitorBarColors[comp],
                  opacity: comp === 'ALDI Nord' ? 1 : 0.55
                }} data-id="element-437" />
                      <span className={`text-xs ${comp === 'ALDI Nord' ? 'font-semibold text-gray-800' : 'text-gray-500'}`} data-id="element-438">
                        {comp}
                      </span>
                    </div>)}
                </div>

                <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3" data-id="element-439">
                  {filteredRegionalData.slice(0, 4).map(r => {
                const isLeading = r['ALDI Nord'] >= Math.max(r.EDEKA, r.REWE, r.LIDL);
                return <div key={r.region} className={`p-3 rounded-lg border ${isLeading ? 'border-green-200 bg-green-50' : 'border-red-100 bg-red-50/50'}`} data-id="element-440">
                        <div className="text-xs font-medium text-gray-600 mb-1" data-id="element-441">
                          {r.region}
                        </div>
                        <div className="flex items-center gap-1.5" data-id="element-442">
                          <span className="text-lg font-bold text-aldi-blue" data-id="element-443">
                            {r['ALDI Nord'].toFixed(1)}
                          </span>
                          <span className="text-xs text-gray-400" data-id="element-444">★</span>
                          {isLeading ? <Badge variant="success" className="text-[10px] py-0" data-id="element-445">
                              Führend
                            </Badge> : <Badge variant="warning" className="text-[10px] py-0" data-id="element-446">
                              Aufholen
                            </Badge>}
                        </div>
                        <div className="text-xs text-gray-400 mt-0.5" data-id="element-447">
                          {r.markets} Märkte
                        </div>
                      </div>;
              })}
                </div>
              </motion.div> : <motion.div key="radius" initial={{
            opacity: 0,
            y: 8
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -8
          }} transition={{
            duration: 0.18
          }} data-id="element-448">
                <div className="flex gap-3 mb-5 flex-wrap" data-id="element-449">
                  <div className="flex-1 min-w-48 relative" data-id="element-450">
                    <MapPin size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" data-id="element-451" />
                    <input type="text" placeholder="Adresse eingeben, z.B. Hamburg, Jungfernstieg" value={addressInput} onChange={e => setAddressInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleSearch()} className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-aldi-blue/20 focus:border-aldi-blue" data-id="element-452" />
                  </div>
                  <div className="flex items-center gap-2" data-id="element-453">
                    <span className="text-sm text-gray-500 whitespace-nowrap" data-id="element-454">
                      Radius:
                    </span>
                    <div className="flex gap-1" data-id="element-455">
                      {radiusOptions.map(r => <button key={r} onClick={() => setSelectedRadius(r)} className={`px-3 py-2 rounded-lg text-xs font-medium transition-all ${selectedRadius === r ? 'bg-aldi-blue text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-id="element-456">
                          {r}
                        </button>)}
                    </div>
                  </div>
                  <button onClick={handleSearch} className="flex items-center gap-2 px-4 py-2.5 bg-aldi-blue text-white rounded-lg text-sm font-medium hover:bg-aldi-dark transition-colors" data-id="element-457">
                    <Search size={14} data-id="element-458" />
                    Suchen
                  </button>
                </div>

                {hasSearched && <motion.div initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} transition={{
              duration: 0.2
            }} data-id="element-459">
                    <div className="text-xs text-gray-500 mb-4 flex items-center gap-1.5" data-id="element-460">
                      <Navigation size={12} data-id="element-461" />
                      <span data-id="element-462">
                        Ergebnisse für{' '}
                        <strong className="text-gray-700" data-id="element-463">
                          {addressSearched}
                        </strong>{' '}
                        im Umkreis von{' '}
                        <strong className="text-gray-700" data-id="element-464">
                          {selectedRadius}
                        </strong>{' '}
                        — {radiusStores.length} Märkte gefunden
                      </span>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5" data-id="element-465">
                      <div data-id="element-466">
                        <div className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-3" data-id="element-467">
                          Ø Bewertung nach Kette
                        </div>
                        <div className="space-y-3" data-id="element-468">
                          {radiusAggregated.sort((a, b) => (b.rating || 0) - (a.rating || 0)).map((item, i) => <div key={item.name} className="flex items-center gap-3" data-id="element-469">
                                <div className="w-2 h-2 rounded-full shrink-0" style={{
                        backgroundColor: competitorBarColors[item.name]
                      }} data-id="element-470" />
                                <div className="w-16 text-sm font-medium text-gray-700" data-id="element-471">
                                  {item.name}
                                </div>
                                <div className="flex-1 h-7 bg-gray-100 rounded-full overflow-hidden relative" data-id="element-472">
                                  <motion.div initial={{
                          width: 0
                        }} animate={{
                          width: `${(item.rating || 0) / 5 * 100}%`
                        }} transition={{
                          duration: 0.5,
                          delay: i * 0.08
                        }} className="h-full rounded-full" style={{
                          backgroundColor: competitorBarColors[item.name],
                          opacity: item.name === 'ALDI Nord' ? 1 : 0.6
                        }} data-id="element-473" />
                                </div>
                                <div className="flex items-center gap-1 w-16 text-right" data-id="element-474">
                                  <span className={`text-sm font-bold ${item.name === 'ALDI Nord' ? 'text-aldi-blue' : 'text-gray-600'}`} data-id="element-475">
                                    {item.rating?.toFixed(1)}
                                  </span>
                                  <Star size={11} className="text-yellow-400 fill-yellow-400" data-id="element-476" />
                                </div>
                                <div className="text-xs text-gray-400 w-14" data-id="element-477">
                                  {item.count} Märkte
                                </div>
                              </div>)}
                        </div>
                      </div>

                      <div data-id="element-478">
                        <div className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-3" data-id="element-479">
                          Einzelne Märkte im Umkreis
                        </div>
                        <div className="space-y-2" data-id="element-480">
                          {radiusStores.map((store, i) => <motion.div key={i} initial={{
                      opacity: 0,
                      x: 10
                    }} animate={{
                      opacity: 1,
                      x: 0
                    }} transition={{
                      delay: i * 0.06
                    }} className={`flex items-center justify-between p-2.5 rounded-lg border ${store.type === 'ALDI Nord' ? 'border-aldi-accent/20 bg-aldi-accent/5' : 'border-gray-100 bg-gray-50'}`} data-id="element-481">
                              <div className="flex items-center gap-2 min-w-0" data-id="element-482">
                                <div className="w-2 h-2 rounded-full shrink-0" style={{
                          backgroundColor: competitorBarColors[store.type]
                        }} data-id="element-483" />
                                <span className={`text-sm truncate ${store.type === 'ALDI Nord' ? 'font-medium text-gray-900' : 'text-gray-600'}`} data-id="element-484">
                                  {store.name}
                                </span>
                              </div>
                              <div className="flex items-center gap-3 shrink-0 ml-2" data-id="element-485">
                                <span className="text-xs text-gray-400" data-id="element-486">
                                  {store.distance}
                                </span>
                                <div className="flex items-center gap-1" data-id="element-487">
                                  <span className={`text-sm font-semibold ${store.type === 'ALDI Nord' ? 'text-aldi-blue' : 'text-gray-600'}`} data-id="element-488">
                                    {store.rating.toFixed(1)}
                                  </span>
                                  <Star size={11} className="text-yellow-400 fill-yellow-400" data-id="element-489" />
                                </div>
                              </div>
                            </motion.div>)}
                        </div>
                      </div>
                    </div>
                  </motion.div>}
              </motion.div>}
          </AnimatePresence>
        </div>
      </Card>
    </div>;
}